import { useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  acceptProposalMutation,
  listInboxOptions,
  listInboxQueryKey,
  listSpacesOptions,
  listWikiFilesQueryKey,
  rejectProposalMutation,
} from "@/api";

export type { Proposal } from "@/api";

/** The caller's pending proposals (facts suggested from their own chats). */
export function useInbox({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ ...listInboxOptions(), enabled, select: (r) => r.data });
}

/** A space's name from the caller's spaces (undefined while loading or unknown). */
export function useSpaceName(spaceUuid: string | null) {
  return useQuery({
    ...listSpacesOptions({ query: { page_size: 100 } }),
    enabled: Boolean(spaceUuid),
    select: (r) => r.data.find((s) => s.uuid === spaceUuid)?.name,
  }).data;
}

/** Spaces the caller can write to: editors and admins (the backend re-checks). */
export function useEditableSpaces() {
  return useQuery({
    ...listSpacesOptions({ query: { page_size: 100 } }),
    select: (r) => r.data.filter((s) => s.currentUserRole === "editor" || s.currentUserRole === "admin"),
  });
}

/** Translate an inbox error by its code; anything else falls back. */
export function useInboxErrorMessage() {
  const { t } = useTranslation("knowledge");
  return (error: unknown, fallback: string): string => {
    if (!(error instanceof ApiError)) return fallback;
    switch (error.code) {
      case "KG_PROPOSAL_NOT_FOUND":
        return t("errors.KG_PROPOSAL_NOT_FOUND");
      case "KG_PROPOSAL_NOT_PENDING":
        return t("errors.KG_PROPOSAL_NOT_PENDING");
      case "KG_PROPOSAL_INVALID":
        return t("errors.KG_PROPOSAL_INVALID", { detail: error.detail ?? "" });
      case "WIKI_CONFLICT":
        return t("errors.WIKI_CONFLICT");
      default:
        return error.detail ?? fallback;
    }
  };
}

export function useRejectProposal() {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const queryClient = useQueryClient();
  const message = useInboxErrorMessage();
  return useMutation({
    ...rejectProposalMutation(),
    onSuccess: () => {
      toast.success(t("inbox.rejected"));
      return queryClient.invalidateQueries({ queryKey: listInboxQueryKey() });
    },
    onError: (error) => toast.error(message(error, t("inbox.rejectFailed"))),
  });
}

/** Accept (optionally edited). Refreshes the inbox, the target space's page list and open pages. */
export function useAcceptProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    ...acceptProposalMutation(),
    onSuccess: (res) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: listInboxQueryKey() }),
        queryClient.invalidateQueries({
          queryKey: listWikiFilesQueryKey({ path: { space_uuid: res.data.spaceUuid } }),
        }),
        // An open page may have gained the fact (and a new revision): refresh it, or the next edit conflicts.
        queryClient.invalidateQueries({
          predicate: (query) => (query.queryKey[0] as { _id?: string } | undefined)?._id === "getWikiFile",
        }),
      ]),
  });
}
